import classNames from 'classnames';
import { getRowPackageLayout } from '..';
import {
  getColumnWidth,
  getHeaderColumnAttrs,
  // getStickyClassName,
} from '../../libs';
import {
  IPagination,
  ITableColumn,
  ITableHeader,
  ITableResizeConfig,
  ITableRowSelection,
} from '../../model';
import { Resizable } from '../../../resizable';
import React, { useEffect, useRef, useState } from 'react';
interface IProps<T> {
  readonly item: ITableColumn<T>;
  readonly index: number;
  readonly columns: ReadonlyArray<ITableColumn<T>>;
  readonly data: ReadonlyArray<T>;
  readonly pagination?: IPagination;
  readonly rowSelection?: ITableRowSelection<T>;
  readonly tableRefElement?: HTMLTableElement;
  readonly onHeaderRow?: (
    columns: ReadonlyArray<ITableColumn<T>>,
    index: number,
    column: ITableColumn<T>
  ) => ITableHeader;
  readonly sortChildren?: React.ReactNode;
  readonly isEnableResize: boolean;
  readonly resizeConfig: ITableResizeConfig;
  readonly onWidthChanged: (index: number, width: number) => void;
}

// 表格头部每一列
export function TableHeaderColumnComponent<T>(props: IProps<T>) {
  const { item, index } = props;
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [stickyLeftLast, setStickyLeftLast] = useState<number | null>(null);
  const [stickyRightFirst, setStickyRightFirst] = useState<number | null>(null);
  // const [itemPosition, setItemPosition] = useState<number>(0);
  const thRef = useRef<HTMLTableHeaderCellElement>(null);
  const resizeConfig: ITableResizeConfig = Object.assign(
    {},
    {
      // 最小宽度默认50
      minimumWidth: 50,
      maximumWidth: 1000,
    },
    props.resizeConfig
  );
  const [initResize, setInitResize] = useState<boolean>(false);

  const handleResize = () => {
    requestAnimationFrame(() => {
      props.columns.forEach((col, colIndex) => {
        const thElement = props.tableRefElement?.querySelector(
          `#column-index-${colIndex + 1}`
        ) as HTMLElement;
        if (thElement && col.fixed) {
          const newPosition = getStickyPositionForColumn(col, colIndex);
          Object.assign(thElement.style, newPosition);
        }
      });
    });
  };

  const getStickyPositionForColumn = (
    column: ITableColumn<T>,
    colIndex: number
  ) => {
    let position = 0;
    const { tableRefElement } = props;
    props.columns.forEach((col, idx) => {
      if (column.fixed === 'left' && idx < colIndex) {
        const prevElement = tableRefElement?.querySelector(
          `#column-index-${idx + 1}`
        ) as HTMLElement;
        const width = prevElement?.offsetWidth || 0;
        position += width;
      } else if (column.fixed === 'right' && idx > colIndex) {
        const nextElement = tableRefElement?.querySelector(
          `#column-index-${idx + 1}`
        ) as HTMLElement;
        const width = nextElement?.offsetWidth || 0;
        position += width;
      }
    });

    if (column.fixed === 'left' && props.rowSelection?.fixed) {
      const selectColumn = props.tableRefElement?.querySelector(
        '.u-table thead > tr > th:nth-child(1)'
      ) as HTMLElement;
      position += selectColumn?.offsetWidth || 0;
    }

    return column.fixed === 'left'
      ? { left: `${position}px` }
      : { right: `${position}px` };
  };

  // 监听 resize 事件
  useEffect(() => {
    let leftLast: number | null = null;
    let rightFirst: number | null = null;
    props.columns.forEach((it, idx) => {
      if (it.fixed === 'left' && (!leftLast || leftLast < idx)) {
        leftLast = idx;
      }
      if (it.fixed === 'right' && (!rightFirst || rightFirst > idx)) {
        rightFirst = idx;
      }
    });
    setStickyLeftLast(leftLast);
    setStickyRightFirst(rightFirst);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.columns]);

  // 处理点击事件
  const onClickHeaderRow = (index: number) => {
    return (e: React.MouseEvent<HTMLDivElement>) => {
      if (!props.onHeaderRow) {
        return;
      }
      const event = props.onHeaderRow(props.columns, index, item);
      if (event.onClick) {
        event.onClick(e);
      }
    };
  };

  const setWidthValue = (width: number) => {
    props.onWidthChanged(index, width);
    setWidth(width);
  };

  const getResizeStyle = (style: React.CSSProperties) => {
    const currentStyle = Object.assign(style, getStickyPosition());
    return Object.assign(currentStyle, props.isEnableResize ? { width } : {});
  };

  // 无colspan属性的单元格为了保证计算的准确性，鼠标移到表头的时候才加载初始化计算resize组件内部元素的宽度
  // 防止直接初始化resize组件，异步的表头加载的时候，布局已经计算完毕
  const onInitResize = () => {
    setInitResize(true);
  };

  // 有colspan属性的单元格需要直接初始化
  useEffect(() => {
    if (attrs.colSpan) {
      setInitResize(true);
    }
  }, []);

  if (item.colSpan === 0) {
    return null;
  }
  const attrs = getHeaderColumnAttrs<T>(item, index);
  if (attrs.colSpan === 0) {
    return null;
  }

  const style = getColumnWidth(item.width);
  const className = classNames(
    'column-item',
    `column-item-${item.key}`,
    `column-index-${index + 1}`,
    stickyLeftLast === index ? 'sticky-left-last' : '',
    stickyRightFirst === index ? 'sticky-right-first' : '',
    item.className
    // getStickyClassName(item.fixed)
  );

  const resizeStyle = attrs.colSpan ? { minWidth: '100%' } : {};
  const resizeConfigProps = Object.assign(
    {},
    resizeConfig,
    attrs.colSpan ? { enableZoomout: false } : {}
  );

  const getStickyPosition = () => {
    let currentStyle = {};
    let currentPosition = 0;
    props.columns.forEach((i, idx) => {
      if (item.fixed === 'left') {
        if (idx < index) {
          const fixedItem = props.tableRefElement?.querySelector(
            `#column-index-${idx + 1}`
          ) as HTMLElement | undefined;
          const itemWidth = fixedItem?.offsetWidth || 0;
          currentPosition += itemWidth;
        }
      } else if (item.fixed === 'right') {
        if (idx > index) {
          const fixedItem = props.tableRefElement?.querySelector(
            `#column-index-${idx + 1}`
          ) as HTMLElement | undefined;
          const itemWidth = fixedItem?.offsetWidth || 0;
          currentPosition += itemWidth;
        }
      }
    });
    if (item.fixed === 'left') {
      if (props.rowSelection) {
        const selectColumn = props.tableRefElement?.querySelector(
          '.u-table thead > tr > th:nth-child(1)'
        ) as HTMLElement | undefined;
        currentPosition += selectColumn?.offsetWidth || 0;
      }

      currentStyle = Object.assign(currentStyle, { left: currentPosition });
    } else if (item.fixed === 'right') {
      currentStyle = Object.assign(currentStyle, { right: currentPosition });
    }
    return currentStyle;
  };

  const renderTableCell = () => {
    if (props.isEnableResize) {
      return (
        <Resizable
          init={initResize}
          style={resizeStyle}
          width={width}
          onResize={setWidthValue}
          onInitResize={setWidthValue}
          {...resizeConfigProps}
        >
          {getRowPackageLayout(
            item.renderHeaderRow
              ? item.renderHeaderRow(
                  item,
                  index,
                  props.columns,
                  props.data,
                  props.pagination
                )
              : item.title,
            props.sortChildren,
            attrs.colSpan ? () => {} : onInitResize
          )}
        </Resizable>
      );
    }
    return getRowPackageLayout(
      item.renderHeaderRow
        ? item.renderHeaderRow(
            item,
            index,
            props.columns,
            props.data,
            props.pagination
          )
        : item.title,
      props.sortChildren
    );
  };

  return (
    <th
      className={classNames(
        className,
        props.isEnableResize ? 'table-draggable' : '',
        item.fixed ? 'sticky-header-item' : ''
      )}
      id={`column-index-${index + 1}`}
      align={item.align}
      style={getResizeStyle(style)}
      key={index}
      {...attrs}
      onClick={onClickHeaderRow(index)}
      ref={thRef}
    >
      {renderTableCell()}
    </th>
  );
}
